import { obtenerMensajeError } from "utils/apiErrors";
import { useState, useCallback, useEffect } from "react";
import dayjs from "dayjs";
import ProcesosApi from "api/procesosApi";
import { formatCurrency } from "utils/currency";

export const useProyeccion = (procesoId, onPropuestaIdChange, tipo_propuesta) => {
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const [propuestaId, setPropuestaIdState] = useState(null);
  const [tieneProyeccion, setTieneProyeccion] = useState(false);
  const [configClases, setConfigClases] = useState({});
  const [cuotaGlobal] = useState(1);
  const [clasesVinculadas, setClasesVinculadas] = useState({});
  const [proyeccionData, setProyeccionData] = useState(null);

  const setPropuestaId = useCallback(
    (id) => {
      setPropuestaIdState(id);
      if (onPropuestaIdChange) {
        onPropuestaIdChange(id);
      }
    },
    [onPropuestaIdChange]
  );

  const obtenerCampoCapital = useCallback((tipo_propuesta) => {
    switch (tipo_propuesta) {
      case "solicitud":
        return "capital";
      case "conciliacion":
        return "capital_conciliado";
      case "acuerdo":
        return "capital_acuerdo";
      default:
        return "capital";
    }
  }, []);

  // ✅ FUNCIÓN HELPER PARA OBTENER EL VALOR DE CAPITAL DE UNA DEUDA SEGÚN EL TIPO DE PROPUESTA
  const obtenerValorCapital = useCallback(
    (deuda, tipo_propuesta) => {
      const campoCapital = obtenerCampoCapital(tipo_propuesta);
      return parseFloat(deuda[campoCapital] || 0);
    },
    [obtenerCampoCapital]
  );

  useEffect(() => {
    const fetchPropuestaExistente = async () => {
      if (!procesoId) return;
      try {
        setLoading(true);
        console.log("Fetching propuesta existente para tipo:", tipo_propuesta);
        const response = await ProcesosApi.obtenerPropuestas(procesoId, tipo_propuesta);
        const propuesta = response.data[0];
        if (propuesta) {
          setPropuestaId(propuesta.id);
          if (propuesta.configuracion) {
            const newConfig = {};
            Object.entries(propuesta.configuracion).forEach(([clase, config]) => {
              newConfig[clase] = {
                cuotaBase: parseFloat(config.cuota_base),
                tasaBase: parseFloat(config.tasa_base),
                cuotaBaseOriginal: parseFloat(config.cuota_base),
                tasaBaseOriginal: parseFloat(config.tasa_base),
                descripcion: config.descripcion || "",
                modificaciones: config.modificaciones || {},
                totalDeuda: parseFloat(config.total_deuda),
                ultimaCuota: config.ultima_cuota || 0,
                ultimaFecha: config.ultima_fecha || null,
                modo_calculo: config.modo_calculo,
                numero_cuotas: config.numero_cuotas,
              };
            });
            setConfigClases(newConfig);
            const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuesta.id);
            if (proyeccionResponse?.data?.proyeccion) {
              setConfigClases((prevConfig) => {
                const updatedConfig = { ...prevConfig };
                Object.entries(proyeccionResponse.data.proyeccion).forEach(([clase, data]) => {
                  if (updatedConfig[clase]) {
                    updatedConfig[clase] = {
                      ...updatedConfig[clase],
                      proyeccion: data.proyeccion,
                    };
                  }
                });
                return updatedConfig;
              });
              setTieneProyeccion(true);
            }
          }
        }
      } catch (error) {
        console.error("Error al obtener propuesta existente:", error);
        setErrorMessage(obtenerMensajeError(error, "Error al cargar la proyección existente"));
      } finally {
        setLoading(false);
      }
    };

    fetchPropuestaExistente();
  }, [procesoId, tipo_propuesta]);

  const generateConfigText = (
    clase,
    cuotaBase,
    tasaBase,
    descripcionAdicional = "",
    configActual,
    vinculaciones
  ) => {
    const parts = [];

    // Verificar si la clase está vinculada
    let claseVinculada = null;
    if (vinculaciones && Object.keys(vinculaciones).length > 0) {
      if (vinculaciones[clase]?.length) {
        claseVinculada = vinculaciones[clase][0];
      } else {
        for (const [key, vinculadas] of Object.entries(vinculaciones)) {
          if (vinculadas.includes(clase)) {
            claseVinculada = key;
            break;
          }
        }
      }
    }

    const modoCalculo = configActual?.[clase]?.modo_calculo;
    const numeroCuotas = configActual?.[clase]?.numero_cuotas;

    if (claseVinculada) {
      // Lógica para clase vinculada
      const configVinculada = configActual[claseVinculada];
      parts.push({
        tipo: "VINCULADA",
        clase: clase,
        claseVinculada: claseVinculada,
        valor: [cuotaBase, configVinculada?.cuotaBase || 0],
        texto: `Ofrezco pagar simultáneamente una cuota mensual por valor de ${
          formatCurrency(cuotaBase).formatted
        } para la ${clase} ${modoCalculo === "POR_CUOTAS" ? `a ${numeroCuotas} cuotas` : ""} y ${
          formatCurrency(configVinculada?.cuotaBase || 0).formatted
        } para la ${claseVinculada} que serán canceladas de acuerdo a la prelación de créditos establecida por la ley`,
      });
    } else {
      // Clase no vinculada
      if (cuotaBase > 0) {
        parts.push({
          tipo: "CUOTA",
          clase: clase,
          valor: cuotaBase,
          texto: `Ofrezco una cuota mensual por valor de ${
            formatCurrency(cuotaBase).formatted
          } para la ${clase} ${
            modoCalculo === "POR_CUOTAS" ? `a ${numeroCuotas} cuotas` : ""
          } que será cancelada de acuerdo a la prelación de créditos establecida por la ley`,
        });
      }

      if (tasaBase > 0) {
        parts.push({
          tipo: "INTERES",
          clase: clase,
          valor: tasaBase,
          texto: `Reconozco un ${tasaBase}% de interés futuro para la ${clase}`,
        });
      }
    }

    if (descripcionAdicional?.trim()) {
      parts.push({
        tipo: "ADICIONAL",
        clase: clase,
        texto: descripcionAdicional.trim(),
      });
    }

    return parts;
  };

  const puedeVincularClases = (clase, claseAVincular, configClases) => {
    // Solo validaciones básicas
    if (!configClases[clase] || !configClases[claseAVincular]) {
      return {
        puede: false,
        mensaje: "Una de las clases no existe en la configuración",
      };
    }

    // Solo verificar que tengan configuración base
    const config1 = configClases[clase];
    const config2 = configClases[claseAVincular];

    const cuota1 = config1?.cuotaBase || config1?.cuota_base;
    const cuota2 = config2?.cuotaBase || config2?.cuota_base;

    if (!cuota1 || !cuota2) {
      return {
        puede: false,
        mensaje: "Las clases deben tener una cuota base configurada",
      };
    }

    // Permitir cualquier combinación
    return { puede: true };
  };

  const handleConfigChange = useCallback(
    (clase, campo, valor) => {
      setConfigClases((prevConfig) => {
        const configActual = prevConfig[clase] || {};
        const nuevaConfig = {
          ...configActual,
          [campo]: valor,
        };
        if (campo === "tasaBase" || campo === "cuotaBase" || campo === "descripcion") {
          nuevaConfig.textoGenerado = generateConfigText(
            clase,
            campo === "cuotaBase" ? valor : configActual.cuotaBase || 0,
            campo === "tasaBase" ? valor : configActual.tasaBase,
            campo === "descripcion" ? valor : configActual.descripcion,
            prevConfig,
            clasesVinculadas
          );
        }
        return {
          ...prevConfig,
          [clase]: nuevaConfig,
        };
      });
    },
    [clasesVinculadas]
  );

  const initializeConfig = useCallback(
    (deudasPorClase) => {
      if (!deudasPorClase || Object.keys(deudasPorClase).length === 0) {
        console.error("No hay deudas por clase para inicializar");
        return;
      }
      console.log("Inicializando configuración con tipo_propuesta:", tipo_propuesta);
      setConfigClases((prevConfig) => {
        // Solo inicializar si no hay configuración previa
        if (Object.keys(prevConfig).length > 0) return prevConfig;

        const newConfig = {};
        Object.entries(deudasPorClase).forEach(([clase, datos]) => {
          const deudasActivas = datos.deudas?.filter((d) => !d.postergado && !d.costas) || [];
          const totalDeudaActiva = deudasActivas.reduce((sum, d) => {
            return sum + obtenerValorCapital(d, tipo_propuesta);
          }, 0);
          // Calcular total de deudas postergadas también con el campo correcto
          const deudasPostergadas = datos.deudas?.filter((d) => d.postergado && !d.costas) || [];
          const totalDeudaPostergada = deudasPostergadas.reduce((sum, d) => {
            return sum + obtenerValorCapital(d, tipo_propuesta);
          }, 0);

          console.log(`Clase ${clase}:`, {
            totalDeudaActiva,
            totalDeudaPostergada,
            campoUsado: obtenerCampoCapital(tipo_propuesta),
            cantidadDeudasActivas: deudasActivas.length,
            cantidadDeudasPostergadas: deudasPostergadas.length,
          });

          newConfig[clase] = {
            cuotaBase: parseFloat(datos.cuotaBase || 0),
            tasaBase: parseFloat(datos.tasaBase || 0),
            cuotaBaseOriginal: parseFloat(datos.cuotaBase || 0),
            tasaBaseOriginal: parseFloat(datos.tasaBase || 0),
            descripcion: "",
            modificaciones: {},
            totalDeuda: totalDeudaActiva,
            totalDeudaPostergada: totalDeudaPostergada,
            ultimaCuota: 0,
            ultimaFecha: null,
            // Cambiar estos valores para forzar CUOTA_FIJA en la primera generación
            modo_calculo: "CUOTA_FIJA", // Forzar CUOTA_FIJA
            numero_cuotas: 0, // Inicializar en 0
          };
        });

        console.log("Nueva configuración inicializada:", newConfig);
        return newConfig;
      });
    },
    [tipo_propuesta, obtenerValorCapital, obtenerCampoCapital]
  );

  const handleModificarCuota = useCallback(
    async (clase, fecha, valor, tasa, numeroCuota) => {
      try {
        const nuevaConfigClase = {
          ...configClases[clase],
          modificaciones: {
            ...configClases[clase].modificaciones,
            [fecha]: {
              cuota: valor,
              tasa: tasa,
              fecha: fecha,
              numeroCuota: numeroCuota,
            },
          },
        };

        const propuestaData = {
          proceso: procesoId,
          fecha_inicio: dayjs().format("YYYY-MM-DD"),
          clase_nombre: clase,
          fecha: fecha,
          nueva_cuota: valor,
          nueva_tasa: tasa,
          numero_cuota: numeroCuota,
          configuracion: {
            ...configClases,
            [clase]: nuevaConfigClase,
          },
          tipo_propuesta: tipo_propuesta,
        };
        const response = propuestaId
          ? await ProcesosApi.actualizarPropuesta(propuestaId, propuestaData)
          : await ProcesosApi.crearPropuesta(procesoId, propuestaData);
        if (response.data?.proyeccion) {
          setConfigClases((prev) => ({
            ...prev,
            [clase]: {
              ...prev[clase],
              ...response.data.configuracion?.[clase],
            },
          }));
          // Obtener la proyección actualizada
          const proyeccionResponse = await ProcesosApi.obtenerProyeccion(propuestaId);
          if (proyeccionResponse?.data) {
            setProyeccionData(proyeccionResponse.data);
            setTieneProyeccion(false);
            setTimeout(() => setTieneProyeccion(true), 0);
          }
        }
        return response;
      } catch (error) {
        console.error("Error en handleModificarCuota:", error);
        throw error;
      }
    },
    [configClases, procesoId, propuestaId, tipo_propuesta]
  );

  const generarProyeccion = useCallback(
    async (deudasPorClase, fechaInicio) => {
      console.log("Iniciando la generacion de la Proyeccion en hook...", {
        deudasPorClase,
        fechaInicio: fechaInicio?.format?.("YYYY-MM-DD"),
        configClases,
        tipo_propuesta,
      });

      if (!deudasPorClase || !fechaInicio) {
        console.error("Faltan datos necesarios:", { deudasPorClase, fechaInicio });
        throw new Error("Faltan datos necesarios para generar proyección");
      }
      setLoading(true);
      setErrorMessage(null);
      try {
        const configuracion = {};
        let ultimaCuotaGlobal = cuotaGlobal;
        let fechaGlobal = dayjs(fechaInicio);

        // Procesar cada clase de deuda
        for (const clase of Object.keys(deudasPorClase)) {
          const configClase = configClases[clase];
          if (!configClase) {
            console.error(`No hay configuración para la clase ${clase}`);
            continue;
          }

          // ✅ USAR EL SALDO INICIAL CORRECTO SEGÚN TIPO_PROPUESTA
          let saldoInicial = 0;
          if (tipo_propuesta === "solicitud") {
            saldoInicial = parseFloat(deudasPorClase[clase].totalDeuda);
          } else if (tipo_propuesta === "conciliacion") {
            saldoInicial = parseFloat(deudasPorClase[clase].totalConciliado);
          } else if (tipo_propuesta === "acuerdo") {
            saldoInicial = parseFloat(deudasPorClase[clase].totalAcuerdo);
          } else {
            // Fallback: usar totalDeuda por defecto
            saldoInicial = parseFloat(deudasPorClase[clase].totalDeuda);
          }

          console.log(
            `Procesando clase ${clase}: saldoInicial=${saldoInicial}, tipo=${tipo_propuesta}`
          );

          let cuotaBase = parseFloat(configClase.cuotaBase);
          let tasaBase = parseFloat(configClase.tasaBase);
          const modificacionesFormateadas = {};
          let numeroCuota = ultimaCuotaGlobal;
          let fechaActual = fechaGlobal.clone();

          // Calcular proyección para esta clase
          let saldo = saldoInicial;
          while (saldoInicial > 0 && numeroCuota < 360) {
            // Límite de seguridad: 30 años
            numeroCuota++;
            const fechaStr = fechaActual.format("YYYY-MM-DD");
            // Aplicar modificaciones si existen
            if (configClase.modificaciones?.[fechaStr]) {
              const mod = configClase.modificaciones[fechaStr];
              cuotaBase = parseFloat(mod.cuota) || cuotaBase;
              tasaBase = parseFloat(mod.tasa) || tasaBase;
            }
            // Cálculo de la cuota
            const tasaMensual = tasaBase / 100;
            const interes = parseFloat((saldo * tasaMensual).toFixed(2));
            const amortizacion = Math.min(cuotaBase - interes, saldo);
            const cuotaFinal = interes + amortizacion;
            modificacionesFormateadas[fechaStr] = {
              cuota: parseFloat(cuotaFinal.toFixed(2)),
              tasa: tasaBase,
              fecha: fechaStr,
              numero_cuota: numeroCuota,
              saldo_anterior: parseFloat(saldo.toFixed(2)),
              interes: interes,
              amortizacion: parseFloat(amortizacion.toFixed(2)),
              saldo_restante: parseFloat((saldo - amortizacion).toFixed(2)),
            };
            saldo = parseFloat((saldo - amortizacion).toFixed(2));
            if (saldo < 0.01) saldo = 0;
            fechaActual = fechaActual.add(1, "month");
          }
          configuracion[clase] = {
            cuota_base: cuotaBase,
            tasa_base: tasaBase,
            descripcion: configClase.descripcion || "",
            total_deuda: saldoInicial,
            modificaciones: modificacionesFormateadas,
            ultima_cuota: numeroCuota,
            ultima_fecha: fechaActual.subtract(1, "month").format("YYYY-MM-DD"),
            modo_calculo: configClase.modo_calculo,
            numero_cuotas: configClase.numero_cuotas,
          };
          ultimaCuotaGlobal = Math.max(ultimaCuotaGlobal, numeroCuota);
          fechaGlobal = fechaActual;
        }
        // Crear o actualizar la propuesta
        const propuestaData = {
          proceso: procesoId,
          fecha_inicio: fechaInicio.format("YYYY-MM-DD"),
          configuracion,
          activa: true,
          tipo_propuesta: tipo_propuesta,
        };
        console.log("Preparando datos de propuesta:", propuestaData);

        const response = propuestaId
          ? await ProcesosApi.actualizarPropuesta(propuestaId, propuestaData)
          : await ProcesosApi.crearPropuesta(procesoId, propuestaData);
        console.log("Respuesta del servidor - Gestion Propuesta:", response);

        if (response.status === 200 || response.status === 201) {
          console.log("Propuesta creada/actualizada exitosamente");
          setPropuestaId(response.data.id);
          const proyeccionResponse = await ProcesosApi.obtenerProyeccion(response.data.id);
          console.log("Respuesta de proyección:", proyeccionResponse);
          if (proyeccionResponse?.data?.proyeccion) {
            console.log("Actualizando estado con nueva proyección");
            setConfigClases((prevConfig) => {
              const newConfig = { ...prevConfig };
              Object.entries(proyeccionResponse.data.proyeccion).forEach(([clase, data]) => {
                if (newConfig[clase]) {
                  newConfig[clase] = {
                    ...newConfig[clase],
                    proyeccion: data.proyeccion,
                  };
                }
              });
              return newConfig;
            });
            setTieneProyeccion(true);
            return response.data;
          }
        }
        throw new Error("Error al procesar la respuesta de la proyección");
      } catch (error) {
        console.error("Error detallado en generarProyeccion:", {
          message: error.message,
          stack: error.stack,
          response: error.response?.data,
        });
        throw error;
      } finally {
        setLoading(false);
      }
    },
    [procesoId, propuestaId, configClases, cuotaGlobal, tipo_propuesta]
  );

  const vincularClases = useCallback(
    async (clase1, clase2) => {
      try {
        const nuevasVinculaciones = { ...clasesVinculadas };

        if (!nuevasVinculaciones[clase1]) {
          nuevasVinculaciones[clase1] = [];
        }
        if (!nuevasVinculaciones[clase2]) {
          nuevasVinculaciones[clase2] = [];
        }

        if (!nuevasVinculaciones[clase1].includes(clase2)) {
          nuevasVinculaciones[clase1].push(clase2);
        }
        if (!nuevasVinculaciones[clase2].includes(clase1)) {
          nuevasVinculaciones[clase2].push(clase1);
        }

        setClasesVinculadas(nuevasVinculaciones);

        return nuevasVinculaciones;
      } catch (error) {
        console.error("Error al vincular clases:", error);
        return {};
      }
    },
    [clasesVinculadas]
  );

  const desvincularClases = useCallback((clase1, clase2) => {
    setClasesVinculadas((prev) => {
      const nuevasVinculaciones = { ...prev };

      if (nuevasVinculaciones[clase1]) {
        nuevasVinculaciones[clase1] = nuevasVinculaciones[clase1].filter((c) => c !== clase2);
        if (nuevasVinculaciones[clase1].length === 0) {
          delete nuevasVinculaciones[clase1];
        }
      }

      if (nuevasVinculaciones[clase2]) {
        nuevasVinculaciones[clase2] = nuevasVinculaciones[clase2].filter((c) => c !== clase1);
        if (nuevasVinculaciones[clase2].length === 0) {
          delete nuevasVinculaciones[clase2];
        }
      }

      return nuevasVinculaciones;
    });

    return {
      [clase1]: [],
      [clase2]: [],
    };
  }, []);

  return {
    loading,
    errorMessage,
    setErrorMessage,
    propuestaId,
    setPropuestaId,
    tieneProyeccion,
    setTieneProyeccion,
    configClases,
    setConfigClases,
    handleConfigChange,
    initializeConfig,
    handleModificarCuota,
    generarProyeccion,
    cuotaGlobal,
    puedeVincularClases,
    clasesVinculadas,
    vincularClases,
    desvincularClases,
    proyeccionData,
    setProyeccionData,
  };
};
